import { useCallback, useMemo, useState } from "react"
import type { InfoGridField } from "./infoGrid.types"

const STORAGE_PREFIX = "info-grid:"

type InfoGridCustomization = {
  order: string[]
  visibility: Record<string, boolean>
  hideEmpty?: boolean
}

function readStorage(id: string): InfoGridCustomization | null {
  try {
    const raw = localStorage.getItem(`${STORAGE_PREFIX}${id}`)
    if (!raw) return null
    return JSON.parse(raw) as InfoGridCustomization
  } catch {
    return null
  }
}

function writeStorage(id: string, state: InfoGridCustomization) {
  localStorage.setItem(`${STORAGE_PREFIX}${id}`, JSON.stringify(state))
}

function removeStorage(id: string) {
  localStorage.removeItem(`${STORAGE_PREFIX}${id}`)
}

export type InfoGridState<TData = unknown> = {
  resolvedFields: InfoGridField<TData>[]
  allFields: InfoGridField<TData>[]
  hideEmpty: boolean
  isFieldVisible(fieldId: string): boolean
  toggleField(fieldId: string): void
  showAllFields(): void
  hideAllFields(): void
  toggleHideEmpty(): void
  reorderFields(fromIndex: number, toIndex: number): void
  reset(): void
  hasCustomization: boolean
}

export function useInfoGridState<TData>(
  id: string,
  fields: InfoGridField<TData>[],
): InfoGridState<TData> {
  const [stored, setStored] = useState<InfoGridCustomization | null>(() =>
    readStorage(id),
  )

  const fieldIds = useMemo(() => fields.map((f) => f.id), [fields])
  const fieldMap = useMemo(() => {
    const map = new Map<string, InfoGridField<TData>>()
    for (const f of fields) map.set(f.id, f)
    return map
  }, [fields])

  const orderedIds = useMemo(() => {
    if (!stored?.order?.length) return fieldIds

    const known = new Set(fieldIds)
    const ordered = stored.order.filter((fid) => known.has(fid))
    const remaining = fieldIds.filter((fid) => !ordered.includes(fid))
    return [...ordered, ...remaining]
  }, [stored, fieldIds])

  const getFieldVisible = useCallback(
    (fieldId: string) => {
      if (stored?.visibility && fieldId in stored.visibility) {
        return stored.visibility[fieldId]
      }
      // Nothing stored: the field's own default decides. `defaultHidden`
      // keeps a rarely-read field out of the panel while leaving it one
      // switch away in the settings list — and "Reset" comes back here.
      return !fieldMap.get(fieldId)?.defaultHidden
    },
    [stored, fieldMap],
  )

  const allFields = useMemo(
    () =>
      orderedIds
        .map((fid) => fieldMap.get(fid))
        .filter(Boolean) as InfoGridField<TData>[],
    [orderedIds, fieldMap],
  )

  const resolvedFields = useMemo(
    () => allFields.filter((f) => getFieldVisible(f.id)),
    [allFields, getFieldVisible],
  )

  const hideEmpty = stored?.hideEmpty ?? true

  const hasCustomization = stored !== null

  const persist = useCallback(
    (next: InfoGridCustomization) => {
      writeStorage(id, next)
      setStored(next)
    },
    [id],
  )

  const toggleField = useCallback(
    (fieldId: string) => {
      const current = stored ?? { order: [], visibility: {} }
      persist({
        ...current,
        order: current.order.length > 0 ? current.order : fieldIds,
        visibility: {
          ...current.visibility,
          [fieldId]: !getFieldVisible(fieldId),
        },
      })
    },
    [stored, persist, fieldIds, getFieldVisible],
  )

  const showAllFields = useCallback(() => {
    const current = stored ?? { order: [], visibility: {} }
    const visibility: Record<string, boolean> = {}
    for (const fid of fieldIds) visibility[fid] = true
    persist({ ...current, visibility })
  }, [stored, persist, fieldIds])

  const hideAllFields = useCallback(() => {
    const current = stored ?? { order: [], visibility: {} }
    const visibility: Record<string, boolean> = {}
    for (const fid of fieldIds) visibility[fid] = false
    persist({ ...current, visibility })
  }, [stored, persist, fieldIds])

  const toggleHideEmpty = useCallback(() => {
    const current = stored ?? { order: [], visibility: {} }
    persist({ ...current, hideEmpty: !hideEmpty })
  }, [stored, persist, hideEmpty])

  const reorderFields = useCallback(
    (fromIndex: number, toIndex: number) => {
      const current = stored ?? { order: [], visibility: {} }
      const currentOrder =
        current.order.length > 0 ? [...current.order] : [...fieldIds]

      const known = new Set(fieldIds)
      const filtered = currentOrder.filter((fid) => known.has(fid))
      const remaining = fieldIds.filter((fid) => !filtered.includes(fid))
      const fullOrder = [...filtered, ...remaining]

      const [moved] = fullOrder.splice(fromIndex, 1)
      fullOrder.splice(toIndex, 0, moved)
      persist({ ...current, order: fullOrder })
    },
    [stored, persist, fieldIds],
  )

  const reset = useCallback(() => {
    removeStorage(id)
    setStored(null)
  }, [id])

  return {
    resolvedFields,
    allFields,
    hideEmpty,
    isFieldVisible: getFieldVisible,
    toggleField,
    showAllFields,
    hideAllFields,
    toggleHideEmpty,
    reorderFields,
    reset,
    hasCustomization,
  }
}
